import { CrmTaskStatus, DealStatus, type CrmTask, type Deal } from "@/types/crm";

export type AttentionReason = "overdue" | "today" | "stale";

export interface AttentionItem {
  kind: "task" | "deal";
  task?: CrmTask;
  deal?: Deal;
  reason: AttentionReason;
  ageDays: number;
  context: string;
}

const MS_PER_DAY = 86_400_000;
const STALE_DAYS = 14;

function startOfDay(date: Date): Date {
  return new Date(date.getFullYear(), date.getMonth(), date.getDate());
}

function wholeDaysBetween(from: Date, to: Date): number {
  return Math.round(
    (startOfDay(to).getTime() - startOfDay(from).getTime()) / MS_PER_DAY
  );
}

function taskContext(task: CrmTask): string {
  return task.projectName || task.contactName || task.dealTypeName || "";
}

export function selectNeedsAttention(
  tasks: CrmTask[],
  deals: Deal[] = [],
  now: Date = new Date(),
  limit = 4
): AttentionItem[] {
  const todayStart = startOfDay(now).getTime();
  const overdue: AttentionItem[] = [];
  const dueToday: AttentionItem[] = [];
  const stale: AttentionItem[] = [];

  for (const task of tasks) {
    if (!task.dueDate || task.status === CrmTaskStatus.Completed) continue;

    const due = new Date(task.dueDate);
    const dueStart = startOfDay(due).getTime();

    if (dueStart < todayStart) {
      overdue.push({
        kind: "task",
        task,
        reason: "overdue",
        ageDays: wholeDaysBetween(due, now),
        context: taskContext(task),
      });
    } else if (due.toDateString() === now.toDateString()) {
      dueToday.push({
        kind: "task",
        task,
        reason: "today",
        ageDays: 0,
        context: taskContext(task),
      });
    }
  }

  for (const deal of deals) {
    if (deal.status !== DealStatus.Open) continue;
    const ageDays = Math.floor(
      (startOfDay(now).getTime() - startOfDay(new Date(deal.updatedAt)).getTime()) / MS_PER_DAY
    );
    if (ageDays < STALE_DAYS) continue;
    stale.push({
      kind: "deal",
      deal,
      reason: "stale",
      ageDays,
      context: deal.dealTypeName || "",
    });
  }

  overdue.sort(
    (a, b) =>
      new Date(a.task!.dueDate!).getTime() - new Date(b.task!.dueDate!).getTime()
  );
  dueToday.sort(
    (a, b) =>
      new Date(a.task!.dueDate!).getTime() - new Date(b.task!.dueDate!).getTime()
  );
  stale.sort((a, b) => b.ageDays - a.ageDays);

  return [...overdue, ...dueToday, ...stale].slice(0, limit);
}
